import { defineStore } from "pinia";
import User from "~/types/UserType";
import { UserApplication } from "~/types/UserApplicationType";

type State = {
  user: User | null;
  userApplications: UserApplication[];
};

export const useUserStore = defineStore("user", {
  state: (): State => ({
    user: null,
    userApplications: [],
  }),
  getters: {
    getUser(state) {
      return state.user;
    },
    getMentionsMonitorSort(state) {
      return (
        state?.user?.preferences?.article_sort?.monitor ?? "relevance:desc"
      );
    },
    isSuperAdmin(state) {
      return state.user?.role_name === "super_admin";
    },
    getUserApplications(state) {
      return state.userApplications;
    },
    hasApplicationAccess(state) {
      return (appName: string) => {
        return state.userApplications.some(
          (app) => app.application === appName,
        );
      };
    },
    // Generate user initials from their name, ex - "John Doe" => "JD", "John" => "JO"
    userInitials(state) {
      const name = state.user?.name;
      if (!name) return "";

      const parts = name.trim().split(/\s+/);
      const first = parts[0]?.[0] ?? "";
      const second =
        parts.length > 1
          ? (parts[parts.length - 1]?.[0] ?? "")
          : (parts[0]?.[1] ?? "");

      return (first + second).toUpperCase();
    },
  },
  actions: {
    setUser(user: User) {
      this.user = user;
    },
    setMentionsMonitorSort(newSortPreference: string) {
      if (this.user) {
        this.user.preferences.article_sort.monitor = newSortPreference;
      }
    },
    setUserApplications(applications: UserApplication[]) {
      this.userApplications = applications;
    },
    async fetchUserApplications() {
      try {
        // Check if applications are already loaded in memory
        if (this.userApplications.length > 0) {
          return { success: true, applications: this.userApplications };
        }

        // Check localStorage cache as fallback
        const cachedApplications = localStorage.getItem("userApplications");
        if (cachedApplications) {
          try {
            const applications = JSON.parse(cachedApplications);
            if (Array.isArray(applications) && applications.length > 0) {
              this.setUserApplications(applications);
              return { success: true, applications };
            }
          } catch (parseError) {
            // eslint-disable-next-line no-console
            console.warn(
              "Failed to parse cached user applications:",
              parseError,
            );
            localStorage.removeItem("userApplications");
          }
        }

        const { useApiCall } = await import("~/composables/useApiCall");
        const response = await useApiCall("/user_applications", {
          method: "GET",
        });

        if (!response.error.value && response.data.value) {
          const applications =
            response.data.value.data?.attributes?.applications || [];
          this.setUserApplications(applications);

          // Also persist to localStorage for cross-session access
          localStorage.setItem(
            "userApplications",
            JSON.stringify(applications),
          );

          return { success: true, applications };
        }

        return { success: false, error: response.error.value };
      } catch (error) {
        // eslint-disable-next-line no-console
        console.warn("Failed to fetch user applications:", error);
        return { success: false, error };
      }
    },
    async endUserImpersonation() {
      if (!this.user?.is_impersonated) {
        return;
      }

      await useApiCall(`/users/${this.user.id}/end_impersonation`, {
        method: "PUT",
      });
    },
  },
});
